import { ContestStatus } from '@betterpool/api-types/contests-service';
import { Group, Stack } from '@mantine/core';
import { IconInfoCircle, IconX, IconMessageCircle2 } from '@tabler/icons-react';
import { format } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useContext, useCallback } from 'react';

import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import { BadgeIcons } from '~/components/shared/Badges/Badges';
import { CurrencyConvertor } from '~/components/utils/formatters';
import { Routes } from '~/domains/common/constants/routes';
import ContestTypeBadge from '~/domains/contest/domains/common/components/ContestTypeBadge/ContestTypeBadge';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';
import ChatWidget from '~/domains/design-system/components/Chat/ChatWidget/ChatWidget';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';

import { ContestChatModalContext } from '../ContestChat/ContestChatModals/ContestChatModalContext';
import { useContestChatContext } from '../ContestChat/ContestChatContext';
import getContestMaxEntriesLabel from '../../utils/getContestMaxEntriesLabel';

import useStyles from './GamecenterHeader.styles';

function formatStartDate(
  startDate: Date,
  status: ContestStatus,
  isEntryDeadlinePending: boolean,
  t: ReturnType<typeof useTranslation<'contest'>>['t']
) {
  if (isEntryDeadlinePending) {
    return t('gamecenter.header.firstTeeTimeTbd');
  }

  if (status !== ContestStatus.SCHEDULED) {
    return format(startDate, 'M/d');
  }

  return t('gamecenter.header.startsAt', { startDate: format(startDate, 'E M/d, h:mm a') });
}

function GamecenterHeader({ closeHref, contest }: { closeHref: string; contest: FullContest }) {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('contest');
  const isEntertainmentOnly = contest.settings?.entertainmentOnlySettings?.enabled;
  const maxEntries = getContestMaxEntriesLabel(contest.entries);
  const isMobile = useIsTablet(); // Use tablet breakpoint to match header visibility logic
  // Contest Huddle modal/panel functionality
  const { openModal, toggleChatPanel } = useContext(ContestChatModalContext);

  // Get chat data from ContestChatContext (single source of truth)
  const { notificationCount, unreadPostsData, isLoadingChats, chats, finalIsChatWebEnabled } =
    useContestChatContext();

  const unreadCount = notificationCount;
  const unreadPostsCount = unreadPostsData?.data?.count || 0;
  const showChat = finalIsChatWebEnabled && !isLoadingChats && chats?.length > 0;

  const handleContestHuddleClick = useCallback(() => {
    if (!contest?.id) return;

    // Mobile: Open modal/drawer (existing behavior)
    if (isMobile) {
      openModal({
        type: 'ContestChatModal',
        toggleChatAction: toggleChatPanel,
      });
    } else {
      toggleChatPanel();
    }
  }, [contest?.id, isMobile, openModal, toggleChatPanel]);

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <Link href={closeHref} className={classes.closeLink} title={t('gamecenter.header.close')}>
          <IconX width={24} height={24} color={theme.white} />
        </Link>
        <div className={classes.desktop}>
          <Group position="apart" w="100%" h="100%">
            <div className={classes.info}>
              <Link href={Routes.contestDetail(contest.id)} className={classes.titleLink}>
                <Group
                  align="center"
                  spacing={theme.other.spacing._8}
                  noWrap
                  className={classes.titleGroup}
                >
                  <Text variant="headline-large" truncate className={classes.titleText}>
                    {contest.name}
                  </Text>
                  <IconInfoCircle size={20} className={classes.titleIcon} />
                </Group>
              </Link>
              <Group c="gray.4" spacing={theme.other.spacing._8}>
                <Text variant="body-medium">
                  {formatStartDate(
                    contest.start_date,
                    contest.status,
                    contest.is_entry_deadline_pending,
                    t
                  )}
                </Text>
                <Text variant="body-medium">•</Text>
                <ContestTypeBadge
                  type={contest.contest_type}
                  altLabel={contest.contest_type_alt_text || contest.contestTypeAltText}
                  colorScheme="dark"
                />
                <BadgeIcons wrapStyle="" tags={contest.tags} iconSize={20} />
              </Group>
            </div>
            <div className={classes.stats}>
              {showChat && (
                <div className={classes.contestHuddleWrapper}>
                  <Button
                    variant="secondary-fill"
                    size="small"
                    onClick={handleContestHuddleClick}
                    className={classes.contestHuddleButton}
                  >
                    <IconMessageCircle2 size={16} />
                    {t('chat:title')}
                  </Button>
                  {/* Unread messages pip */}
                  {unreadCount > 0 && (
                    <div className={classes.notificationPip}>
                      <div className={classes.orangeDot} />
                    </div>
                  )}
                </div>
              )}

              <div className={classes.stat}>
                <Text variant="headline-medium" c="white">
                  {contest.entries.filled}/{maxEntries}
                </Text>
                <Text variant="body-medium" c="gray.4">
                  {typeof contest.entries.user === 'number'
                    ? t('gamecenter.header.entries', {
                        filled: contest.entries.user,
                        max: contest.entries.max_per_user,
                      })
                    : t('gamecenter.header.entriesAnon')}
                </Text>
              </div>
              {!isEntertainmentOnly && (
                <>
                  <div className={classes.stat}>
                    <Text variant="headline-medium" c="white">
                      {CurrencyConvertor(contest.entry_fee / 100)}
                    </Text>
                    <Text variant="body-medium" c="gray.4">
                      {t('gamecenter.header.entry')}
                    </Text>
                  </div>
                  <div className={classes.stat}>
                    <Text variant="headline-medium" c="white">
                      {CurrencyConvertor(contest.prize_pool / 100)}
                    </Text>
                    <Text variant="body-medium" c="gray.4">
                      {t('gamecenter.header.prizes')}
                    </Text>
                  </div>
                </>
              )}
            </div>
          </Group>
        </div>
        <div className={classes.mobile}>
          <Stack
            align="flex-start"
            justify="center"
            h="100%"
            spacing={theme.other.spacing._4}
            w="100%"
          >
            <Text variant="headline-large" c="white" lh={1.2} truncate maw="100%">
              {contest.name}
            </Text>
            <Text variant="body-small" c="gray.4" lh={1} mt={-theme.other.spacing._2}>
              {formatStartDate(
                contest.start_date,
                contest.status,
                contest.is_entry_deadline_pending,
                t
              )}
            </Text>
          </Stack>
        </div>
        <div className={classes.mobile}>
          <Link href={Routes.contestDetail(contest.id)} className={classes.mobileInfoLink}>
            <IconInfoCircle size={24} color="white" />
          </Link>
        </div>
      </div>

      {/* Chat Widget - positioned at bottom on mobile, hidden on desktop */}
      {isMobile && showChat && (
        <ChatWidget
          title={t('chat:title')}
          newPostCount={unreadPostsCount}
          unreadMessageCount={unreadCount}
          onClose={handleContestHuddleClick}
          isMinimized
        />
      )}
    </div>
  );
}

export default GamecenterHeader;
